"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";

import type {
  FacelessWorkflowPreset,
  SeoCtaContext,
} from "@/content/seo/types";
import { appendFacelessWorkflowPreset } from "@/utils/facelessWorkflowPreset";
import {
  trackSeoEvent,
  type SeoEventPayload,
} from "@/utils/seoAnalytics";
import { getLocalizedPath } from "@/utils/utils";
import { updateActivationCtaContext } from "@/utils/activationAttribution";

type SeoCtaLinkProps = {
  locale: string;
  source: string;
  location: string;
  children: ReactNode;
  className?: string;
  href?: string;
  landingPath?: string;
  preset?: FacelessWorkflowPreset;
  analyticsProps?: Pick<
    SeoEventPayload,
    "input_type" | "duration_bucket" | "aspect_ratio"
  >;
};

export default function SeoCtaLink({
  locale,
  source,
  location,
  children,
  className,
  href: destinationHref,
  landingPath,
  preset,
  analyticsProps,
}: SeoCtaLinkProps) {
  const pathname = usePathname();
  const localePrefix = locale === "en" ? "" : `/${locale}`;
  const currentLandingPath =
    landingPath ||
    (localePrefix && pathname.startsWith(localePrefix)
      ? pathname.slice(localePrefix.length) || "/"
      : pathname || "/");
  const context: SeoCtaContext = {
    source,
    landingPath: currentLandingPath,
    ctaLocation: location,
    locale,
    intent: "faceless-video",
  };
  const query = new URLSearchParams({
    intent: context.intent,
    source: context.source,
    landingPath: context.landingPath,
    location: context.ctaLocation,
    locale: context.locale,
  });
  appendFacelessWorkflowPreset(query, preset);
  const signupHref = `${getLocalizedPath("/signup", locale)}?${query.toString()}`;
  const href = destinationHref || signupHref;

  const handleClick = () => {
    try {
      window.sessionStorage.setItem(
        "ariaflow:seo-cta-context",
        JSON.stringify(context),
      );
      updateActivationCtaContext({
        storage: window.localStorage,
        source: context.source,
        landingPath: context.landingPath,
        ctaLocation: context.ctaLocation,
        locale: context.locale,
      });
    } catch {
      // Analytics context is optional and must never block navigation.
    }

    trackSeoEvent("seo_cta_click", {
      ...analyticsProps,
      source: context.source,
      page: context.landingPath,
      location: context.ctaLocation,
      locale: context.locale,
      intent: context.intent,
    });
  };

  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}
